/**
 * ChatMessage Component
 *
 * Single message component with comprehensive display features:
 * - Sender avatar with initials or image
 * - Sender name with type indicator (user/agent/system)
 * - Timestamp with relative and absolute formatting
 * - Message content with @mention highlighting
 * - Reply-to thread indicator
 * - Agent status badge
 * - Message grouping support
 *
 * @module components/chat/ChatMessage
 */

import { memo, useMemo, useCallback, useState } from 'react';
import {
  Reply,
  CornerUpLeft,
  Circle,
  ExternalLink,
  Copy,
  Check,
  FileText,
  Download
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChat } from '../../contexts/ChatContext.jsx';
import { Avatar, AGENT_STATUS_COLORS, AGENT_STATUS_LABELS } from '../common/Avatar.jsx';

// ============================================
// Constants
// ============================================

/**
 * Sender name colors
 */
const SENDER_NAME_COLORS = {
  user: 'text-emerald-400',
  agent: 'text-indigo-400',
  system: 'text-gray-400'
};

/**
 * Time thresholds for relative time formatting (in milliseconds)
 */
const TIME_THRESHOLDS = {
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000
};

// ============================================
// Helper Functions
// ============================================

/**
 * Format timestamp for display
 * @param {string|Date} date - Date to format
 * @param {boolean} [showRelative=true] - Whether to show relative time
 * @returns {object} { display: string, full: string }
 */
function formatTimestamp(date, showRelative = true) {
  const messageDate = new Date(date);
  const now = new Date();
  const diff = now.getTime() - messageDate.getTime();

  // Full timestamp for tooltip
  const full = messageDate.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  // Relative or time-only display
  if (!showRelative) {
    return {
      display: messageDate.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit'
      }),
      full
    };
  }

  if (diff < TIME_THRESHOLDS.MINUTE) {
    return { display: 'just now', full };
  }

  if (diff < TIME_THRESHOLDS.HOUR) {
    const minutes = Math.floor(diff / TIME_THRESHOLDS.MINUTE);
    return { display: `${minutes}m ago`, full };
  }

  if (diff < TIME_THRESHOLDS.DAY) {
    // Same day - show time only
    return {
      display: messageDate.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit'
      }),
      full
    };
  }

  if (diff < TIME_THRESHOLDS.WEEK) {
    // Within a week - show day and time
    return {
      display: messageDate.toLocaleDateString(undefined, {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }),
      full
    };
  }

  // Older - show date
  return {
    display: messageDate.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    }),
    full
  };
}

/**
 * Parse message content and extract @mentions
 * @param {string} content - Message content
 * @param {string[]} [mentionedUserIds] - User IDs mentioned
 * @param {string[]} [mentionedAgentIds] - Agent IDs mentioned
 * @returns {Array} Array of content segments with mention flags
 */
function parseMessageContent(content, mentionedUserIds = [], mentionedAgentIds = []) {
  if (!content) return [];

  const mentionRegex = /@(\w+)/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastIndex, match.index)
      });
    }

    // Add mention
    segments.push({
      type: 'mention',
      content: match[0],
      name: match[1]
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastIndex)
    });
  }

  return segments.length > 0 ? segments : [{ type: 'text', content }];
}

// ============================================
// Sub-Components
// ============================================

/**
 * Agent status badge component
 * @param {object} props - Component props
 * @param {string} props.status - Agent operational status
 * @param {string} [props.project] - Current project name
 * @returns {React.ReactElement} Status badge
 */
const AgentStatusBadge = memo(function AgentStatusBadge({ status, project }) {
  const statusColor = AGENT_STATUS_COLORS[status] || AGENT_STATUS_COLORS.offline;
  const statusLabel = AGENT_STATUS_LABELS[status] || 'Unknown';

  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs bg-gray-800 border border-gray-700"
      title={project ? `${statusLabel} on ${project}` : statusLabel}
    >
      <Circle className={`w-2 h-2 ${statusColor} fill-current`} />
      <span className="text-gray-400">{statusLabel}</span>
      {project && (
        <span className="text-gray-500 truncate max-w-[100px]">
          &middot; {project}
        </span>
      )}
    </div>
  );
});

/**
 * Reply-to thread indicator
 * @param {object} props - Component props
 * @param {object} props.replyTo - Reply-to message data
 * @param {Function} [props.onClick] - Click handler to jump to message
 * @returns {React.ReactElement} Reply indicator
 */
const ReplyIndicator = memo(function ReplyIndicator({ replyTo, onClick }) {
  const truncatedContent = useMemo(() => {
    if (!replyTo?.content) return 'Original message';
    return replyTo.content.length > 50
      ? `${replyTo.content.slice(0, 50)}...`
      : replyTo.content;
  }, [replyTo?.content]);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1 mb-1 text-xs text-gray-400 bg-gray-800/50 rounded border-l-2 border-gray-600 hover:bg-gray-800 hover:border-gray-500 transition-colors"
      title="Click to jump to original message"
    >
      <CornerUpLeft className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">
        {replyTo?.senderName && (
          <span className="font-medium text-gray-300 mr-1">
            {replyTo.senderName}:
          </span>
        )}
        {truncatedContent}
      </span>
    </button>
  );
});

/**
 * Message content with Markdown rendering and @mention highlighting
 * @param {object} props - Component props
 * @param {string} props.content - Message content
 * @param {string[]} [props.mentionedUserIds] - User IDs mentioned
 * @param {string[]} [props.mentionedAgentIds] - Agent IDs mentioned
 * @param {Function} [props.onMentionClick] - Mention click handler
 * @returns {React.ReactElement} Rendered content
 */
const MessageContent = memo(function MessageContent({
  content,
  mentionedUserIds,
  mentionedAgentIds,
  onMentionClick
}) {
  // Custom component to render text with @mentions highlighted
  const TextWithMentions = useCallback(({ children }) => {
    if (typeof children !== 'string') {
      return <>{children}</>;
    }

    const segments = parseMessageContent(children, mentionedUserIds, mentionedAgentIds);

    return (
      <>
        {segments.map((segment, index) => {
          if (segment.type === 'mention') {
            return (
              <button
                key={index}
                type="button"
                onClick={() => onMentionClick?.(segment.name)}
                className="text-indigo-400 hover:text-indigo-300 hover:underline font-medium transition-colors"
              >
                {segment.content}
              </button>
            );
          }
          return <span key={index}>{segment.content}</span>;
        })}
      </>
    );
  }, [mentionedUserIds, mentionedAgentIds, onMentionClick]);

  return (
    <div className="text-sm text-gray-300 break-words prose prose-sm prose-invert max-w-none prose-p:my-1 prose-pre:my-2 prose-code:text-orange-300 prose-code:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-gray-800 prose-pre:border prose-pre:border-gray-700 prose-pre:rounded-lg">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Render paragraphs with mention support
          p: ({ children }) => (
            <p className="my-1">
              <TextWithMentions>{children}</TextWithMentions>
            </p>
          ),
          // Code blocks - react-markdown v9+ doesn't have 'inline' prop anymore
          // We detect code blocks by checking if className has a language or content has newlines
          code: ({ node, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const codeString = String(children).replace(/\n$/, '');
            const isBlock = match || codeString.includes('\n');

            if (!isBlock) {
              // Inline code
              return (
                <code className="bg-gray-800 px-1.5 py-0.5 rounded text-orange-300 text-xs" {...props}>
                  {children}
                </code>
              );
            }
            // Code block
            return (
              <pre className="bg-gray-800 border border-gray-700 rounded-lg p-3 overflow-x-auto my-2">
                <code className="text-xs text-gray-300" {...props}>
                  {children}
                </code>
              </pre>
            );
          },
          // Links
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-400 hover:text-indigo-300 hover:underline"
              {...props}
            >
              {children}
            </a>
          ),
          // Lists
          ul: ({ children }) => <ul className="list-disc list-inside my-1 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside my-1 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="text-gray-300">{children}</li>,
          // Headings
          h1: ({ children }) => <h1 className="text-lg font-bold text-white my-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-base font-bold text-white my-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-bold text-white my-1">{children}</h3>,
          // Blockquote
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-gray-600 pl-3 my-2 text-gray-400 italic">
              {children}
            </blockquote>
          ),
          // Strong/Bold
          strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
          // Emphasis/Italic
          em: ({ children }) => <em className="italic text-gray-200">{children}</em>,
        }}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
});

/**
 * Message timestamp component
 * @param {object} props - Component props
 * @param {string} props.date - Date string
 * @param {boolean} [props.showRelative=true] - Show relative time
 * @returns {React.ReactElement} Timestamp
 */
const Timestamp = memo(function Timestamp({ date, showRelative = true }) {
  const { display, full } = useMemo(
    () => formatTimestamp(date, showRelative),
    [date, showRelative]
  );

  return (
    <time
      dateTime={new Date(date).toISOString()}
      className="text-xs text-gray-500"
      title={full}
    >
      {display}
    </time>
  );
});

/**
 * Message actions overlay (copy, reply, etc.)
 * @param {object} props - Component props
 * @param {Function} [props.onReply] - Reply action handler
 * @param {Function} [props.onCopy] - Copy action handler
 * @param {string} props.content - Message content for copying
 * @returns {React.ReactElement} Actions overlay
 */
const MessageActions = memo(function MessageActions({ onReply, onCopy, content }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Copy failed
    }
  }, [content]);

  return (
    <div className="absolute -top-3 right-2 flex items-center gap-1 px-1 py-0.5 bg-gray-800 border border-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
      {onReply && (
        <button
          type="button"
          onClick={onReply}
          className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          title="Reply"
        >
          <Reply className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={onCopy || handleCopy}
        className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
        title={copied ? 'Copied!' : 'Copy message'}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-green-500" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
});

// ============================================
// Attachments Component
// ============================================

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size string
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * MessageAttachments Component
 *
 * Displays attachments for a message (images and files)
 *
 * @param {object} props - Component props
 * @param {object[]} props.attachments - Attachment objects
 * @returns {React.ReactElement} Attachments display
 */
const MessageAttachments = memo(function MessageAttachments({ attachments }) {
  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter((a) => a.mimeType?.startsWith('image/'));
  const files = attachments.filter((a) => !a.mimeType?.startsWith('image/'));

  return (
    <div className="mt-2 space-y-2">
      {/* Image attachments - grid layout */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((attachment) => (
            <a
              key={attachment.id}
              href={`/api/attachments/${attachment.id}/view`}
              target="_blank"
              rel="noopener noreferrer"
              className="relative group block max-w-xs overflow-hidden rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
            >
              <img
                src={`/api/attachments/${attachment.id}/view`}
                alt={attachment.originalName}
                className="max-w-full max-h-48 object-contain bg-gray-900"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <ExternalLink className="w-6 h-6 text-white" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1 text-xs text-gray-300 truncate">
                {attachment.originalName}
              </div>
            </a>
          ))}
        </div>
      )}

      {/* File attachments - list layout */}
      {files.length > 0 && (
        <div className="flex flex-col gap-1">
          {files.map((attachment) => (
            <a
              key={attachment.id}
              href={`/api/attachments/${attachment.id}/download`}
              download={attachment.originalName}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800/50 border border-gray-700 hover:border-gray-500 hover:bg-gray-800 transition-colors max-w-xs group"
            >
              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-200 truncate">
                  {attachment.originalName}
                </div>
                <div className="text-xs text-gray-500">
                  {formatFileSize(attachment.size)}
                </div>
              </div>
              <Download className="w-4 h-4 text-gray-500 group-hover:text-gray-300 flex-shrink-0" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
});

// ============================================
// Main Component
// ============================================

/**
 * ChatMessage Component
 *
 * Displays a single chat message with full features.
 *
 * @param {object} props - Component props
 * @param {object} props.message - Message object
 * @param {string} props.message.id - Message ID
 * @param {string} props.message.senderId - Sender ID
 * @param {string} props.message.senderType - Sender type (user/agent/system)
 * @param {string} props.message.senderName - Sender display name
 * @param {string} props.message.content - Message content
 * @param {string} [props.message.replyToId] - Reply-to message ID
 * @param {string} props.message.createdAt - Creation timestamp
 * @param {string[]} [props.message.mentionedUserIds] - Mentioned user IDs
 * @param {string[]} [props.message.mentionedAgentIds] - Mentioned agent IDs
 * @param {object} [props.replyTo] - Reply-to message data for preview
 * @param {boolean} [props.isGrouped=false] - Whether message is grouped with previous
 * @param {boolean} [props.isOwn=false] - Whether message is from current user
 * @param {boolean} [props.showActions=true] - Whether to show hover actions
 * @param {boolean} [props.showAvatar=true] - Whether to show avatar
 * @param {boolean} [props.showAgentStatus=true] - Whether to show agent status badge
 * @param {string} [props.avatarUrl] - Sender avatar URL
 * @param {Function} [props.onReply] - Reply action handler
 * @param {Function} [props.onMentionClick] - Mention click handler
 * @param {Function} [props.onReplyClick] - Reply indicator click handler
 * @param {Function} [props.onClick] - Message click handler
 * @param {string} [props.className] - Additional CSS classes
 * @returns {React.ReactElement} Chat message
 */
function ChatMessage({
  message,
  replyTo,
  isGrouped = false,
  isOwn = false,
  showActions = true,
  showAvatar = true,
  showAgentStatus = true,
  avatarUrl,
  onReply,
  onMentionClick,
  onReplyClick,
  onClick,
  className = ''
}) {
  // ----------------------------------------
  // Context
  // ----------------------------------------

  const { getAgentStatus } = useChat();

  // ----------------------------------------
  // Computed Values
  // ----------------------------------------

  const {
    id,
    senderId,
    senderType,
    senderName,
    content,
    replyToId,
    createdAt,
    mentionedUserIds,
    mentionedAgentIds,
    attachments
  } = message;

  /**
   * Get agent status if sender is an agent
   */
  const agentStatus = useMemo(() => {
    if (senderType !== 'agent') return null;
    const status = getAgentStatus(senderId);
    return status?.operationalStatus || null;
  }, [senderType, senderId, getAgentStatus]);

  /**
   * Agent's current project
   */
  const agentProject = useMemo(() => {
    if (senderType !== 'agent') return null;
    const status = getAgentStatus(senderId);
    return status?.currentProject || null;
  }, [senderType, senderId, getAgentStatus]);

  /**
   * Sender name color based on type
   */
  const nameColorClass = SENDER_NAME_COLORS[senderType] || SENDER_NAME_COLORS.user;

  // ----------------------------------------
  // Handlers
  // ----------------------------------------

  const handleReply = useCallback(() => {
    onReply?.(message);
  }, [message, onReply]);

  const handleReplyClick = useCallback(() => {
    onReplyClick?.(replyToId);
  }, [replyToId, onReplyClick]);

  const handleClick = useCallback(() => {
    onClick?.(message);
  }, [message, onClick]);

  // ----------------------------------------
  // Render - Grouped Message (Compact)
  // ----------------------------------------

  if (isGrouped) {
    return (
      <div
        className={`
          group relative px-4 py-0.5 hover:bg-gray-800/30 transition-colors
          ${className}
        `}
        data-message-id={id}
        onClick={handleClick}
      >
        <div className="flex items-start gap-3 max-w-3xl">
          {/* Spacer for avatar alignment */}
          <div className="w-8 flex-shrink-0 flex items-center justify-center">
            <span className="text-xs text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
              {formatTimestamp(createdAt, false).display}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 ml-0">
            {/* Reply indicator */}
            {replyToId && replyTo && (
              <ReplyIndicator replyTo={replyTo} onClick={handleReplyClick} />
            )}

            {/* Message content */}
            <MessageContent
              content={content}
              mentionedUserIds={mentionedUserIds}
              mentionedAgentIds={mentionedAgentIds}
              onMentionClick={onMentionClick}
            />

            {/* Attachments */}
            <MessageAttachments attachments={attachments} />
          </div>
        </div>

        {/* Hover actions */}
        {showActions && (
          <MessageActions
            onReply={onReply ? handleReply : undefined}
            content={content}
          />
        )}
      </div>
    );
  }

  // ----------------------------------------
  // Render - Full Message
  // ----------------------------------------

  return (
    <div
      className={`
        group relative px-4 pt-2 pb-1 hover:bg-gray-800/30 transition-colors
        ${className}
      `}
      data-message-id={id}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3 max-w-3xl">
        {/* Avatar */}
        {showAvatar && (
          <Avatar
            name={senderName}
            type={senderType}
            avatarUrl={avatarUrl}
            status={agentStatus}
            size="md"
          />
        )}

        {/* Message body */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-0.5">
            {/* Sender name */}
            <span className={`text-sm font-semibold ${nameColorClass}`}>
              {senderName || 'Unknown'}
            </span>

            {/* Agent status badge */}
            {showAgentStatus && senderType === 'agent' && agentStatus && (
              <AgentStatusBadge status={agentStatus} project={agentProject} />
            )}

            {/* Timestamp */}
            <Timestamp date={createdAt} showRelative />

            {/* Own message indicator */}
            {isOwn && (
              <span className="text-xs text-emerald-500/70 font-medium">
                (you)
              </span>
            )}
          </div>

          {/* Reply indicator */}
          {replyToId && replyTo && (
            <ReplyIndicator replyTo={replyTo} onClick={handleReplyClick} />
          )}

          {/* Message content */}
          <MessageContent
            content={content}
            mentionedUserIds={mentionedUserIds}
            mentionedAgentIds={mentionedAgentIds}
            onMentionClick={onMentionClick}
          />

          {/* Attachments */}
          <MessageAttachments attachments={attachments} />
        </div>
      </div>

      {/* Hover actions */}
      {showActions && (
        <MessageActions
          onReply={onReply ? handleReply : undefined}
          content={content}
        />
      )}
    </div>
  );
}

// ============================================
// Memoized Export
// ============================================

const MemoizedChatMessage = memo(ChatMessage);

// ============================================
// Named Exports
// ============================================

export default MemoizedChatMessage;

export {
  ChatMessage,
  AgentStatusBadge,
  ReplyIndicator,
  MessageContent,
  MessageAttachments,
  Timestamp,
  MessageActions,
  formatTimestamp,
  formatFileSize,
  parseMessageContent,
  SENDER_NAME_COLORS,
  TIME_THRESHOLDS
};
